import { useState, useRef, useEffect } from 'react'
import { Globe, Check } from 'lucide-react'
import { useLanguage } from '@/contexts/LanguageContext'

interface Language {
  code: string
  name: string
  flag: string
}

const LANGUAGES: Language[] = [
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
]

interface LanguageSelectorProps {
  variant?: 'full' | 'compact' | 'minimal'
  className?: string
}

export default function LanguageSelector({ variant = 'full', className = '' }: LanguageSelectorProps) {
  const { currentLanguage, supportedLanguages, setLanguage, t } = useLanguage()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Filter languages to only show supported ones
  const availableLanguages = LANGUAGES.filter(lang => supportedLanguages.includes(lang.code))
  const selectedLanguage = LANGUAGES.find(lang => lang.code === currentLanguage)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleLanguageChange = (langCode: string) => {
    setLanguage(langCode)
    setIsOpen(false)

    // Reload the page with new language parameter
    const url = new URL(window.location.href)
    url.searchParams.set('lang', langCode)
    window.location.href = url.toString()
  }

  if (availableLanguages.length <= 1) {
    // Don't show selector if only one language is available
    return null
  }

  // Minimal variant (just flag + dropdown)
  if (variant === 'minimal') {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-white/80 backdrop-blur-sm hover:bg-white transition-colors shadow-sm border border-gray-200"
          aria-label={t('language.select')}
        >
          <span className="text-xl">{selectedLanguage?.flag}</span>
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-[9999] animate-fadeIn">
            {availableLanguages.map(lang => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors ${
                  lang.code === currentLanguage ? 'bg-blue-50' : ''
                }`}
              >
                <span className="text-2xl">{lang.flag}</span>
                <span className="flex-1 text-left font-medium text-gray-900">{lang.name}</span>
                {lang.code === currentLanguage && (
                  <Check size={18} className="text-blue-600" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  // Compact variant (flag + code)
  if (variant === 'compact') {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white hover:bg-gray-50 transition-colors shadow-sm border border-gray-200"
        >
          <span className="text-xl">{selectedLanguage?.flag}</span>
          <span className="text-sm font-semibold text-gray-700 uppercase">{currentLanguage}</span>
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-[9999] animate-fadeIn">
            {availableLanguages.map(lang => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors ${
                  lang.code === currentLanguage ? 'bg-blue-50' : ''
                }`}
              >
                <span className="text-2xl">{lang.flag}</span>
                <span className="flex-1 text-left font-medium text-gray-900">{lang.name}</span>
                {lang.code === currentLanguage && (
                  <Check size={18} className="text-blue-600" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  // Full variant (default)
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white hover:bg-gray-50 transition-all shadow-md hover:shadow-lg border border-gray-200"
      >
        <Globe size={20} className="text-blue-600" />
        <span className="text-xl">{selectedLanguage?.flag}</span>
        <span className="font-semibold text-gray-900">{selectedLanguage?.name}</span>
        <svg
          className={`w-4 h-4 text-gray-600 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-[9999] animate-fadeIn">
          <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <Globe size={16} />
              {t('language.select')}
            </div>
          </div>
          {availableLanguages.map(lang => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors ${
                lang.code === currentLanguage ? 'bg-blue-50' : ''
              }`}
            >
              <span className="text-2xl">{lang.flag}</span>
              <div className="flex-1 text-left">
                <div className="font-medium text-gray-900">{lang.name}</div>
                <div className="text-xs text-gray-500 uppercase">{lang.code}</div>
              </div>
              {lang.code === currentLanguage && (
                <Check size={20} className="text-blue-600" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
